import {
  Button,
  Toolbar,
  ToolbarItem,
  ToolbarSeparator,
} from "@progress/kendo-react-buttons";
import { Fragment } from "react";
import { useSelector } from "react-redux";
import {
  Chart,
  ChartSeries,
  ChartSeriesItem,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  ChartValueAxis,
  ChartValueAxisItem,
  ChartTooltip,
} from "@progress/kendo-react-charts";
import { DonutCharts } from "../../../components/dashboards";
import { formatDate } from "../../../lib/utils";

import i18next from "i18next";
import { useTranslation } from "react-i18next";

export const Dashboard = (props) => {
  const currentTheme = useSelector((state) => state.currentTheme);
  const currentUser = useSelector((state) => state.currentUser);
  const pageState = useSelector((state) => state.accountManagementPage);
  const id = pageState.formValues.SubscriptionId;

  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage || i18n.language;
  const currency = pageState.formValues?.CurrentSubscription?.Currency;

  const formatCurrency = (amount) => {
    const options = {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    };

    try {
      return new Intl.NumberFormat(locale, {
        ...options,
        style: "currency",
        currency,
      }).format(amount);
    } catch {
      const formattedAmount = new Intl.NumberFormat(locale, options).format(
        amount,
      );
      return currency ? `${formattedAmount} ${currency}` : formattedAmount;
    }
  };

  const toolbarRenderer = () => {
    return (
      <Toolbar
        style={{
          borderColor: "transparent",
          backgroundColor: "transparent",
          paddingTop: "0",
        }}
      >
        <Fragment>
          {currentUser.IsServiceSubscriptionRootUser && (
            <>
              <ToolbarItem>
                <Button
                  style={{
                    color: currentTheme.primary,
                    border: 0,
                    paddingLeft: 0,
                    backgroundColor: "transparent",
                  }}
                  icon="cancel"
                  onClick={(e) => props.onCancelSubsciption()}
                >
                  {t("accountmang.tabs.cancelsub")}
                </Button>
              </ToolbarItem>
              {/* <ToolbarSeparator style={{ borderColor: "#000" }} /> */}
            </>
          )}
          {/* <ToolbarItem>
            <Button
              disabled={pageState.processing}
              style={{
                color: currentTheme.primary,
                border: 0,
                backgroundColor: "transparent",
              }}
              icon="x"
              onClick={(e) => props.onCloseAccountManagement()}
            >
              {t("accountmang.tabs.close")}
            </Button>
          </ToolbarItem> */}
        </Fragment>
      </Toolbar>
    );
  };

  const Essentials = (props) => {
    return (
      <div
        className="account-dashboard-essentials"
        style={{
          fontFamily: "Arial, sans-serif",
          border: "1px solid #ccc",
          padding: "16px",
          margin: "0 0 20px",
        }}
      >
        <h5>{t("accountmang.tabs.essentials")}</h5>
        <div
          className="account-dashboard-essentials-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "8px",
          }}
        >
          <div>
            <strong>{t("accountmang.tabs.subid")}</strong> {currentUser?.Subscription?.Id}
          </div>
          <div>
            <strong>{t("accountmang.tabs.subname")}</strong>{" "}
            <a style={{ color: `${currentTheme.touchColor}`, cursor: "auto" }}>
              {currentUser?.Subscription?.Name
                ? currentUser?.Subscription?.Name
                : pageState.formValues?.CurrentSubscription?.PlanName}
            </a>
          </div>
          <div>
            <strong>{t("accountmang.tabs.billperiod")}</strong>{" "}
            {formatDate(
              pageState.formValues?.CurrentSubscription?.SDate,
              "dd-mm-yyyy"
            )}{" "}
            -{" "}
            {formatDate(
              pageState.formValues?.CurrentSubscription?.EDate,
              "dd-mm-yyyy"
            )}
          </div>
          <div>
            <strong>{t("accountmang.tabs.planname")}</strong>{" "}
            {pageState.formValues?.CurrentSubscription?.PlanName}
          </div>
          <div>
            <strong>{t("accountmang.tabs.currency")}</strong>{" "}
            {pageState.formValues?.CurrentSubscription?.Currency}
          </div>
          <div>

            <strong>{t("accountmang.tabs.stat")}</strong>{" "}
            {pageState.formValues?.CurrentSubscription?.IsActive ? (
              <span style={{ color: `${currentTheme.success}` }}>{t("accountmang.tabs.active")}</span>
            ) : (
              <span style={{ color: `${currentTheme.error}` }}>{t("accountmang.tabs.inactive")}</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const LatestBilledAmountCard = (props) => {
    const latestInvoice = [...pageState.formValues.Billings].sort(
      (a, b) => new Date(b.PaymentDate) - new Date(a.PaymentDate)
    )[0];

    if (!latestInvoice) return null; // Handle empty data

    return (
      <div
        className="account-dashboard-card account-dashboard-latest-invoice"
        style={{
          border: "1px solid #ddd",
          padding: "16px",
          width: "300px",
          backgroundColor: "#fff",
        }}
      >
        <h5>{t("accountmang.tabs.latest")}</h5>
        <h3>{latestInvoice.AmountDue}</h3>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "5px",
            color: "#0078D4",
            marginBottom: "8px",
          }}
        >
          {/* Replace SvgIcon with a bell icon if needed */}
          <span>🔔</span>
          <h6>
            {t("accountmang.tabs.invoice")} {latestInvoice.InvoiceId} {t("accountmang.tabs.generated")}{" "}
            {new Date(latestInvoice.PaymentDate).toLocaleDateString()}
          </h6>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          <Button
            variant="contained"
            style={{
              padding: 8,
              color: currentTheme.touchColor,
              fontWeight: 500,
            }}
            sx={{ textTransform: "none" }}
            onClick={() => window.open(latestInvoice.InvoiceUrl, "_blank")}
          >
            {t("accountmang.tabs.download")}
          </Button>
          {/* <Button variant="outlined" sx={{ textTransform: "none" }}>
            View Invoices
          </Button> */}
        </div>
      </div>
    );
  };

  const InvoicesOverTime = (props) => {
    const billings = pageState?.formValues?.Billings || [];

    if (!billings.length) return null; 
    // Prepare chart data
    const data = billings.map((item) => {
      const amount = item?.AmountDue
        ? parseFloat(item.AmountDue.replace(/[^\d.-]/g, ""))
        : 0;
      return isNaN(amount) ? 0 : amount;
    });

    const categories = billings.map((item) => {
      const date = item?.PaymentDate ? new Date(item.PaymentDate) : null;
      return date instanceof Date && !isNaN(date)
        ? date.toLocaleDateString(locale, {
            day: "2-digit",
            month: "2-digit",
          })
        : "";
    });

    const totalAmount = data.reduce((sum, value) => sum + value, 0);

    return (
      <div
        className="account-dashboard-card account-dashboard-invoices-over-time"
        style={{
          border: "1px solid #ddd",
          padding: "16px",
          backgroundColor: "#fff",
          width: "350px",
        }}
      >
        {/* Title */}
        <h5 style={{ marginBottom: "16px" }}>{t("accountmang.tabs.invoices")}</h5>

        {/* Chart */}
        <Chart style={{ height: "200px" }}>
          <ChartCategoryAxis>
            <ChartCategoryAxisItem
              categories={categories}
              line={{ visible: true }}
              labels={{ visible: true }}
              majorGridLines={{ visible: true }}
              color={currentTheme?.primary || "#007bff"}
            />
          </ChartCategoryAxis>
          <ChartValueAxis>
            <ChartValueAxisItem
              labels={{ visible: true, format: "{0}" }}
              majorGridLines={{ visible: true }}
              line={{ visible: true }}
              color={currentTheme?.primary || "#007bff"}
            />
          </ChartValueAxis>
          <ChartTooltip format={`{0:n2}${currency ? ` ${currency}` : ""}`} />
          <ChartSeries>
            <ChartSeriesItem
              type="column"
              data={data}
              color={currentTheme?.touchColor || "#007bff"}
              gap={2}
            />
          </ChartSeries>
        </Chart>

        {/* Total Amount */}
        <div style={{ marginTop: "30px" }}>
          <p
            style={{
              color: currentTheme?.touchColor || "#007bff",
              fontWeight: "bold",
              margin: "0",
            }}
          >
            {t("accountmang.tabs.amount")}
          </p>
          <h2 style={{ margin: "0", fontWeight: "bold" }}>
            {formatCurrency(totalAmount)}
          </h2>
        </div>
      </div>
    );
  };

  const transformSize = (valueInMB) => {
    if (!valueInMB || isNaN(parseFloat(valueInMB))) return valueInMB;

    const format = (val, unit) => `${Math.round(val * 100) / 100} ${unit}`;

    if (valueInMB < 1024) {
      return format(valueInMB, "MB");
    } else if (valueInMB < 1024 * 1024) {
      return format(valueInMB / 1024, "GB");
    } else {
      return format(valueInMB / (1024 * 1024), "TB");
    }
  };

  const DatabaseStorageChart = () => {
    // Data values
    const usedSpace = pageState.formValues?.Consumption?.DatabaseUsedSizeInMB; // in GB
    const remainingSpace =
      pageState.formValues?.Consumption?.DatabaseRemainingSizeInMB;
    const allocatedSpace =
      pageState.formValues?.Consumption?.DatabaseTotalSizeInMB;
    const maxStorageSpace =
      pageState.formValues?.Consumption?.DatabaseSubscriptionMaximumSizeInMB;

    // Convert MB → GB (rounded to 2 decimals)
    const toGB = (mb) =>
      mb && !isNaN(mb) ? Math.round((mb / 1024) * 100) / 100 : 0;

    // Data for the chart
    const data = [
      {
        category: "Used",
        value: toGB(usedSpace),
        color: currentTheme.touchColor,
      },
      { category: "Remaining", value: toGB(remainingSpace), color: "#D3D3D3" },
    ];

    return (
      <div
        className="account-dashboard-storage"
        style={{
          margin: "20px 0 0",
          width: "100%",
          textAlign: "center",
          border: "1px solid #ddd",
          padding: "16px",
          display: "flex",
        }}
      >
        <div
          className="account-dashboard-storage-chart"
          style={{ width: "100%", display: "flex", justifyContent: "center" }}
        >
          <DonutCharts
            title={t("accountmang.tabs.datastorage")}
            data={data}
            currentTheme={currentTheme}
            legendPosition="bottom"
            labelContent={(e) => e.value}
            widthChart="100%"
          />
        </div>
        <div
          className="account-dashboard-storage-details"
          style={{
            width: "100%",
            display: "flex",
            alignContent: "center",
            alignItems: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "5px 10px",
            }}
          >
            <span
              style={{
                borderRight: `1px solid ${currentTheme.touchColor}`,
                textAlign: "right",
                paddingRight: "5px",
              }}
            >
              <strong>{t("accountmang.tabs.used")}</strong>
            </span>
            <span style={{ textAlign: "left", paddingLeft: "5px" }}>
              {transformSize(usedSpace)}
            </span>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "5px 10px",
            }}
          >
            <span
              style={{
                paddingRight: "5px",
                textAlign: "right",
                borderRight: `1px solid ${currentTheme.touchColor}`,
              }}
            >
              <strong>{t("accountmang.tabs.remaining")}</strong>
            </span>
            <span style={{ textAlign: "left", paddingLeft: "5px" }}>
              {transformSize(remainingSpace)}
            </span>
          </div>
          <div
            style={{
              display: "flex",
              margin: "5px 10px",
              textAlign: "center",
            }}
          >
            <span
              style={{
                paddingRight: "5px",
                textAlign: "right",
                borderRight: `1px solid ${currentTheme.touchColor}`,
              }}
            >
              <strong>{t("accountmang.tabs.allocated")}</strong>
            </span>
            <span style={{ textAlign: "left", paddingLeft: "5px" }}>
              {transformSize(allocatedSpace)}
            </span>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "5px 10px",
            }}
          >
            <span
              style={{
                paddingRight: "5px",
                textAlign: "right",
                borderRight: `1px solid ${currentTheme.touchColor}`,
              }}
            >
              <strong>{t("accountmang.tabs.maxstorage")}</strong>
            </span>
            <span style={{ textAlign: "left", paddingLeft: "5px" }}>
              {transformSize(maxStorageSpace)}
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="account-dashboard">
      <h5>{t("accountmang.tabs.dashboard")}</h5>
      {toolbarRenderer()}
      <Essentials props={props} />

      {currentUser.IsServiceSubscriptionRootUser && (
        <div className="account-dashboard-billing-cards" style={{ display: "flex" }}>
          <LatestBilledAmountCard props={props} />
          <div className="account-dashboard-spacer" style={{ width: "20px" }}></div>
          <InvoicesOverTime props={props} />
        </div>
      )}

      <DatabaseStorageChart props={props} />

      <div className="account-dashboard-usage-cards" style={{ display: "flex" }}>
        <div
          className="account-dashboard-usage-card"
          style={{
            margin: "20px 0 0",
            textAlign: "center",
            border: "1px solid #ddd",
            padding: "16px",
          }}
        >
          <DonutCharts
            title={t("accountmang.tabs.reports")}
            currentTheme={currentTheme}
            data={[
              {
                category: "Used",
                value: pageState.formValues?.Consumption?.UsedReports,
                color: currentTheme.touchColor,
              },
              {
                category: "Remaining",
                value: pageState.formValues?.Consumption?.RemainingReports,
                color: "#D3D3D3",
              },
            ]}
            labelContent={(e) => e.value}
          />
        </div>

        <div className="account-dashboard-spacer" style={{ width: "20px" }}></div>
        <div
          className="account-dashboard-usage-card"
          style={{
            margin: "20px 0 0",
            textAlign: "center",
            border: "1px solid #ddd",
            padding: "16px",
          }}
        >
          <DonutCharts
            title={t("accountmang.tabs.users")}
            currentTheme={currentTheme}
            data={[
              {
                category: "Used",
                value: pageState.formValues?.Consumption?.UsedUsers,
                color: currentTheme.touchColor,
              },
              {
                category: "Remaining",
                value: pageState.formValues?.Consumption?.RemainingUsers,
                color: "#D3D3D3",
              },
            ]}
            labelContent={(e) => e.value}
          />{" "}
        </div>
        <div className="account-dashboard-spacer" style={{ width: "20px" }}></div>
        <div
          className="account-dashboard-usage-card"
          style={{
            margin: "20px 0 0",
            textAlign: "center",
            border: "1px solid #ddd",
            padding: "16px",
          }}
        >
          <DonutCharts
            title={t("accountmang.tabs.alerts")}
            currentTheme={currentTheme}
            data={[
              {
                category: "Used",
                value: pageState.formValues?.Consumption?.UsedAlerts,
                color: currentTheme.touchColor,
              },
              {
                category: "Remaining",
                value: pageState.formValues?.Consumption?.RemaingAlerts,
                color: "#D3D3D3",
              },
            ]}
            labelContent={(e) => e.value}
          />
        </div>
      </div>
      <style>
        {`
          .accountmanagement-page .k-card .k-card-title{
              font-size:16px;
              font-weight:bold;
              color:${currentTheme.touchColor}
          }
          .accountmanagement-page .k-card .k-card-subtitle{
              font-size:14px;
              color:${currentTheme.primary}
          }

          @media (max-width: 1024px) {
            .accountmanagement-page .account-dashboard,
            .accountmanagement-page .account-dashboard * {
              box-sizing: border-box;
            }

            .accountmanagement-page .account-dashboard-essentials-grid {
              grid-template-columns: 1fr !important;
            }

            .accountmanagement-page .account-dashboard-billing-cards,
            .accountmanagement-page .account-dashboard-usage-cards,
            .accountmanagement-page .account-dashboard-storage,
            .accountmanagement-page .account-dashboard-storage-details {
              flex-direction: column;
              width: 100%;
            }

            .accountmanagement-page .account-dashboard-card,
            .accountmanagement-page .account-dashboard-usage-card {
              margin-left: 0 !important;
              margin-right: 0 !important;
              width: 100% !important;
            }

            .accountmanagement-page .account-dashboard-storage-chart,
            .accountmanagement-page .account-dashboard-storage-details {
              width: 100% !important;
            }

            .accountmanagement-page .account-dashboard-spacer {
              display: none;
            }
          }
        `}
      </style>
    </div>
  );
};
